// src/components/ProfileDropdown.jsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useOutsideClick } from "../hooks/useOutsideClick";
import { useDispatch } from "react-redux";
import { logout } from "../store/slices/authSlice";
import { useTranslation } from "react-i18next";
import ConfirmLogoutModal from "./ConfirmLogoutModal";

const UserIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

function ProfileDropdown({ avatarUrl, userName }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dropdownRef = useRef();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { t } = useTranslation();

  useOutsideClick(dropdownRef, () => setShowDropdown(false));

  const handleConfirmLogout = () => {
    dispatch(logout());
    localStorage.setItem("profilePicture", "");
    setShowConfirm(false);
    setShowDropdown(false);
    navigate("/");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="w-8 h-8 rounded-full border border-gray-500 overflow-hidden p-0 flex items-center justify-center bg-[#333]"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="User Avatar"
            width="32"
            height="32"
            className="w-full h-full object-cover rounded-full"
          />
        ) : (
          <div className="w-full h-full flex items-center bg-[#333] justify-center rounded-full">
            <UserIcon className="text-white w-4 h-4" />
          </div>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-60 bg-[#2C2C2C] text-white rounded-md shadow-lg border border-[#444] z-50">
          <div className="flex flex-col items-center p-4 border-b border-[#444]">
            <div
              className="w-12 h-12 rounded-full bg-[#333] flex items-center justify-center overflow-hidden cursor-pointer"
              onClick={() => {
                navigate("/profile");
                setShowDropdown(false);
              }}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="User Avatar"
                  width="48"
                  height="48"
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserIcon className="text-white w-6 h-6" />
              )}
            </div>

            <p className="mt-2 text-sm truncate font-bold">
              {userName || "Пользователь"}
            </p>
          </div>

          <ul className="flex flex-col p-2 gap-2 text-sm">
            <li
              className="cursor-pointer text-[#AAA] px-3 py-2 hover:bg-[#1E1E1E] rounded flex justify-between"
              onClick={() => {
                navigate("/messages");
                setShowDropdown(false);
              }}
            >
              {t("messages")}
            </li>
            <li
              className="cursor-pointer text-[#AAA] px-3 py-2 hover:bg-[#1E1E1E] rounded"
              onClick={() => {
                navigate("/bookmarks");
                setShowDropdown(false);
              }}
            >
              {t("bookmarks")}
            </li>
            <li>
              <hr className="border-gray-600" />
            </li>
            <li
              className="cursor-pointer px-3 py-2 hover:bg-[#1E1E1E] rounded text-red-400"
              onClick={() => {
                setShowConfirm(true);
              }}
            >
              {t("logout")}
            </li>
          </ul>
        </div>
      )}

      <ConfirmLogoutModal
        open={showConfirm}
        onConfirm={handleConfirmLogout}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}

export default ProfileDropdown;
